import type React from 'react';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const MODAL_ROOT_ID = 'demostack-ui-modal';

export const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

interface PortalProps {
  children: React.ReactNode;
  rootId?: string;
  anchorRef?: React.RefObject<HTMLElement>;
  zIndex?: number;
}

const createModalParent = (id: string, zIndex) => {
  const parentElement = document.createElement('div');
  parentElement.setAttribute('id', id);
  parentElement.setAttribute('class', 'absolute w-full h-full inset-0');
  parentElement.style.setProperty('z-index', zIndex);

  return parentElement;
};

const addModalParent = (rootElem: any) => {
  document.body.insertBefore(
    rootElem,
    document.body.lastElementChild?.nextElementSibling
  );
};

export const Portal = ({
  children,
  rootId = MODAL_ROOT_ID,
  anchorRef,
  zIndex = 1000,
}: PortalProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const modalTarget = modalRef.current;
    let modalParent = anchorRef
      ? anchorRef.current
      : document.querySelector(`#${rootId}`);
    if (!modalParent) {
      modalParent = createModalParent(rootId, zIndex);
      addModalParent(modalParent);
    }
    modalTarget && modalParent.appendChild(modalTarget);

    return () => {
      modalTarget?.remove();
      if (modalParent && modalParent.childNodes.length === 0) {
        modalParent.remove();
      }
    };
  }, [modalRef, rootId, anchorRef, zIndex]);

  const getModalRef = () => {
    if (!modalRef.current) {
      modalRef.current = document.createElement('div');
    }
    return modalRef.current;
  };

  return ReactDOM.createPortal(children, getModalRef());
};
